import React, { useState } from 'react';
import {
    Dialog, DialogFooter, DialogType, PrimaryButton, DefaultButton, Dropdown, IDropdownOption,
    MessageBar, MessageBarType, Spinner, Stack, Text,
} from '@fluentui/react';
import {
    useAddProductsToPromotionMutation, useGetPromotionsQuery,
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import { IPromotion, describePromoPricing } from '../../models/ProductPromo';

interface IProps {
    catalog: string;
    storefront: string;
    productIds: string[];
    onDismiss: () => void;
    onApplied: () => void;
}

/**
 * Puts already-selected products onto an EXISTING campaign.
 *
 * Distinct from Create Promo, which starts a new one. Without this, adding a single product to a
 * running promotion meant recreating the whole campaign.
 *
 * Only the link is written — no markup, window or scope — so a product added here immediately inherits
 * whatever the campaign currently says, and any later edit to it moves this product too.
 */
const AddToPromotionDialog: React.FC<IProps> = (props) => {
    const promotions = useGetPromotionsQuery({
        catalog: props.catalog,
        storefront: props.storefront,
        // Includes paused and scheduled campaigns deliberately: adding products to a promotion that has
        // not started yet is a normal thing to want, and this dialog is not the place to hide it.
        includeInactive: true,
    });
    const [addProducts, addState] = useAddProductsToPromotionMutation();

    const [promotionId, setPromotionId] = useState<string | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [result, setResult] = useState<string | undefined>();

    const options: IDropdownOption[] = (promotions.data ?? []).flatMap((p: IPromotion) => p.id
        ? [{
            key: p.id,
            // What it charges is on the option, because that is what the choice is actually between:
            // the products inherit the campaign's pricing the moment they are added.
            text: `${p.name ?? 'Untitled'}${p.active === false ? ' (paused)' : ''}, ${describePromoPricing(p.markupType, p.markup)}, ${p.productCount ?? 0} product(s)`,
        }]
        : []);

    const apply = async () => {
        if (!promotionId) return;
        setError(undefined);
        setResult(undefined);
        try {
            const response = await addProducts({
                catalog: props.catalog,
                id: promotionId,
                request: { productIds: props.productIds },
            }).unwrap();

            // Partial success is normal — a product already on the campaign is skipped, not an error —
            // so the counts are reported rather than collapsed into a single success/failure.
            if (response.updated > 0 && response.skipped.length === 0) {
                props.onApplied();
                return;
            }
            setResult(response.skipped.length > 0
                ? `${response.updated} added. ${response.skipped.length} skipped: ${response.skipped[0].reason}`
                : response.message ?? 'Nothing was added.');
            if (response.updated > 0) props.onApplied();
        } catch {
            setError('Could not add the products to the promotion.');
        }
    };

    return (
        <Dialog
            hidden={false}
            onDismiss={props.onDismiss}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Add to Promotion',
                subText: `Add ${props.productIds.length} selected product(s) to an existing promotion.`,
            }}
            minWidth={480}
        >
            <Stack tokens={{ childrenGap: '0.75em' }}>
                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                {result && <MessageBar messageBarType={MessageBarType.warning}>{result}</MessageBar>}

                {promotions.isLoading ? (
                    <Spinner label='Loading promotions…' />
                ) : options.length === 0 ? (
                    <MessageBar messageBarType={MessageBarType.info}>
                        No promotions exist yet. Use Create Promo to start one.
                    </MessageBar>
                ) : (
                    <>
                        <Dropdown
                            label='Promotion'
                            placeholder='Choose a promotion'
                            selectedKey={promotionId}
                            options={options}
                            onChange={(_, option) => setPromotionId(option?.key as string)}
                        />
                        <Text variant='small' style={{ color: '#605e5c' }}>
                            These products inherit the promotion&rsquo;s markup, window and storefronts. Editing
                            the promotion later moves them with it.
                        </Text>
                    </>
                )}
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    text={addState.isLoading ? 'Adding…' : 'Add'}
                    disabled={!promotionId || addState.isLoading || options.length === 0}
                    onClick={apply}
                />
                <DefaultButton text='Cancel' onClick={props.onDismiss} disabled={addState.isLoading} />
            </DialogFooter>
        </Dialog>
    );
};

export default AddToPromotionDialog;
